import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import Navbar from "./Navbar";
import { ArrowRosette } from "./Logo";

const CONTACT_URL =
  process.env.REACT_APP_CONTACT_URL || `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

const SUBJECTS = [
  { value: "cotacao", label: "Solicitação de cotação" },
  { value: "importacao", label: "Importação" },
  { value: "exportacao", label: "Exportação" },
  { value: "assessoria", label: "Assessoria aduaneira" },
  { value: "outro", label: "Outro assunto" },
];

const inputCls =
  "w-full border border-white/25 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/35 transition-colors duration-300 focus:border-white focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50";

const INITIAL = { name: "", email: "", phone: "", subject: "cotacao", message: "" };

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(CONTACT_URL, form);
      toast.success("Mensagem enviada! A equipe JVF retornará em breve.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="contact-page">
      <Navbar />
      <main className="grid gap-16 px-6 pb-24 pt-32 lg:grid-cols-2 lg:px-12 lg:pt-40">
        <div className="flex flex-col items-start gap-8">
          <a
            href="/"
            data-testid="contact-back-link"
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-white"
          >
            <ArrowLeft size={14} />
            Voltar para o site
          </a>
          <h1 className="font-oswald text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            Solicite uma cotação
          </h1>
          <p className="max-w-md text-base font-light leading-relaxed text-white/60 lg:text-lg">
            Preencha os seus dados e conte o que a sua operação precisa. A mensagem
            chega diretamente à nossa equipe e respondemos por e-mail ou telefone.
          </p>
          <ArrowRosette className="mt-4 w-36 opacity-70" />
          <div className="flex flex-col gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/50">
            <span data-testid="contact-page-phone">(53) 3201-8201</span>
            <span data-testid="contact-page-email">contato@jvfdespachos.com.br</span>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="border border-white/15 p-8 lg:p-12"
          data-testid="contact-form"
        >
          <div className="flex flex-col gap-7">
            <div>
              <label htmlFor="name" className={labelCls}>
                Nome completo
              </label>
              <input
                id="name"
                name="name"
                required
                minLength={2}
                value={form.name}
                onChange={onChange}
                placeholder="Seu nome"
                className={inputCls}
                data-testid="contact-form-name-input"
              />
            </div>
            <div className="grid gap-7 sm:grid-cols-2">
              <div>
                <label htmlFor="email" className={labelCls}>
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="voce@empresa.com.br"
                  className={inputCls}
                  data-testid="contact-form-email-input"
                />
              </div>
              <div>
                <label htmlFor="phone" className={labelCls}>
                  Telefone / WhatsApp
                </label>
                <input
                  id="phone"
                  name="phone"
                  required
                  minLength={8}
                  value={form.phone}
                  onChange={onChange}
                  placeholder="(53) 99999-0000"
                  className={inputCls}
                  data-testid="contact-form-phone-input"
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className={labelCls}>
                Assunto
              </label>
              <select
                id="subject"
                name="subject"
                value={form.subject}
                onChange={onChange}
                className={`${inputCls} appearance-none`}
                data-testid="contact-form-subject-select"
              >
                {SUBJECTS.map((s) => (
                  <option key={s.value} value={s.value} className="bg-navy text-white">
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="message" className={labelCls}>
                Mensagem
              </label>
              <textarea
                id="message"
                name="message"
                required
                minLength={10}
                rows={5}
                value={form.message}
                onChange={onChange}
                placeholder="Descreva a sua necessidade: tipo de carga, origem/destino, prazos..."
                className={`${inputCls} resize-none`}
                data-testid="contact-form-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="flex items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:bg-white/85 disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? "Enviando..." : "Enviar mensagem"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ContactPage;
